<template>
  <div
    class="pagination-container d-flex justify-content-between align-items-center"
  >
    <!-- Items per page selector -->
    <div class="d-flex align-items-center">
      <label class="me-2">Show:</label>
      <select
        class="form-select form-select-sm"
        style="width: auto"
        v-model="localPerPage"
        @change="handlePerPageChange"
      >
        <option v-for="option in perPageOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
      <span class="ms-2">
        Showing {{ startItem }} - {{ endItem }} of {{ total }} records
      </span>
    </div>

    <!-- Pagination controls -->
    <nav aria-label="Page navigation">
      <ul class="pagination mb-0">
        <!-- First page -->
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a class="page-link" href="#" @click.prevent="changePage(1)">
            <i class="fas fa-angle-double-left"></i>
          </a>
        </li>

        <!-- Previous page -->
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a
            class="page-link"
            href="#"
            @click.prevent="changePage(currentPage - 1)"
          >
            <i class="fas fa-angle-left"></i>
          </a>
        </li>

        <!-- Page numbers -->
        <li
          v-for="page in visiblePages"
          :key="page === '...' ? `ellipsis-${page}` : page"
          class="page-item"
          :class="{
            active: page === currentPage,
            disabled: page === '...',
          }"
        >
          <a
            v-if="page !== '...'"
            class="page-link"
            href="#"
            @click.prevent="changePage(page)"
          >
            {{ page }}
          </a>
          <span v-else class="page-link">...</span>
        </li>

        <!-- Next page -->
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a
            class="page-link"
            href="#"
            @click.prevent="changePage(currentPage + 1)"
          >
            <i class="fas fa-angle-right"></i>
          </a>
        </li>

        <!-- Last page -->
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a class="page-link" href="#" @click.prevent="changePage(totalPages)">
            <i class="fas fa-angle-double-right"></i>
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
export default {
  name: "Pagination",
  props: {
    total: {
      type: Number,
      required: true,
    },
    perPage: {
      type: Number,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 20, 25, 50, 100, 1000],
    },
  },
  data() {
    return {
      localPerPage: this.perPage,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.perPage);
    },
    startItem() {
      return this.total === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.perPage, this.total);
    },
    visiblePages() {
      const pages = [];
      for (let i = 1; i <= this.totalPages; i++) {
        if (
          i === 1 ||
          i === this.totalPages ||
          (i >= this.currentPage - 2 && i <= this.currentPage + 2)
        ) {
          pages.push(i);
        } else if (i === this.currentPage - 3 || i === this.currentPage + 3) {
          pages.push("...");
        }
      }
      return pages;
    },
  },
  methods: {
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$emit("page-changed", page);
      }
    },
    handlePerPageChange() {
      this.$emit("per-page-changed", this.localPerPage);
    },
  },
  watch: {
    perPage(newVal) {
      this.localPerPage = newVal;
    },
  },
};
</script>

<style scoped>
.pagination-container {
  margin-top: 1rem;
  padding: 0.5rem;
}

.page-link {
  cursor: pointer;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}
</style>
